import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Table from '../../../components/Table';
import { InsuranceAppendix } from '../../../interfaces/InsuranceAppendix';
import { AppendixPremiumColumn, AppendixBranchColumn, AppendixInsuredPersonColumn, AppendixCoverageAmountColumn } from './insuranceProductColumns';
import { InsurancePolicy, InsurancePolicyMainBranch, InsurancePolicyStatuses } from '../../../interfaces/InsurancePolicy';
import { DocumentType } from '../../../interfaces/SignedDocument';
import { useTranslation } from 'react-i18next';
import FormSelectionCard from '../../../components/FormSelectionCard';
import { Company } from '../../../interfaces/Company';
import { editInsurancePolicy } from '../../../utils/api/insurancePolicy';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { useProductsContext } from './insuranceProductsContext';
import { editExistingAppendix } from './insuranceCreationAndEdit/appendixAndPolicyCreationFunctions';
import { FormInput } from '../../../components';
import { de } from 'date-fns/locale';

interface PolicyRowTableExpansionProps {
    appendices: InsuranceAppendix[];
    policy: InsurancePolicy;
}

const PolicyRowTableExpansion = ({ policy, appendices }: PolicyRowTableExpansionProps) => {
    const { t } = useTranslation('insurance');
    const { insurancePolicies, setInsurancePolicies, client, consts } = useProductsContext();
    const [appendixToEdit, setAppendixToEdit] = useState<InsuranceAppendix | null>(null);
    const [showEditAppendixModal, setShowEditAppendixModal] = useState(false);
    const [selectedForms, setSelectedForms] = useState<string[]>([]);
    const [selectedQuestionnaireForms, setSelectedQuestionnaireForms] = useState<string[]>([]);
    const [generalForms, setGeneralForms] = useState<string[]>([]);
    const [questionnaireForms, setQuestionnaireForms] = useState<string[]>([]);
    useEffect(() => {
        if (appendixToEdit) {
            setShowEditAppendixModal(true);
        }
    }, [appendixToEdit]);

    useEffect(() => {
        console.log('appendices', appendices);
    }, [appendices]);

    useEffect(() => {
        if (policy.templates?.quastionnaires) {
            setSelectedQuestionnaireForms(policy.templates?.quastionnaires);
        }

        if (policy.templates?.generalTemplates) {
            setSelectedForms(policy.templates?.generalTemplates);
        }

        const company = policy.company as Company;
        const templates = company.signnowTemplates[policy.branch as keyof typeof company.signnowTemplates];
        setQuestionnaireForms(templates?.quastionnaires || []);

        const generalForms: string[] = [];
        for (const key in templates) {
            if (key !== 'quastionnaires') {
                const key1 = key as keyof typeof templates;
                if (Array.isArray(templates[key1])) {
                    generalForms.push(...(templates[key1] as string[]));
                } else {
                    generalForms.push(templates[key1] as string);
                }
            }
        }
        setGeneralForms(generalForms);
    }, [policy]);

    useEffect(() => {
        if (!showEditAppendixModal) {
            setAppendixToEdit(null);
        }
    }, [showEditAppendixModal]);

    const headerStyle = {
        backgroundColor: 'rgb(0,0,0, 0.05)',
    };

    const rowStyle = (row: any) => {
        const appendix = row.original;
        const styles: any = {};
        if (appendix.isNew) {
            styles.backgroundColor = 'rgb(198, 236, 227, 0.65)';
        }
        return styles;
    };

    const items: { label: string; Component: any; extraData?: any }[] = [];

    const AppendixCancellationColumn = ({ row: { original: appendix } }: { row: { original: InsuranceAppendix } }) => {
        const [isCancelled, setIsCancelled] = useState<boolean>(appendix.isCancelled || false);

        const onChange = (e: ChangeEvent<HTMLInputElement>) => {
            const isCancelled = e.target.checked;
            setIsCancelled(isCancelled);
            editExistingAppendix({
                ...appendix,
                isCancelled,
            });
        };

        return (
            <FormInput
                type="checkbox"
                name="isCancelled"
                checked={isCancelled}
                onChange={onChange}
                disabled={policy.status !== InsurancePolicyStatuses.Initial}
            />
        );
    };

    // Determine if the policy branch requires coverage amount column (life/criticalIllness/mortgageInsurance)
    const requiresCoverageAmount =
        policy.branch === InsurancePolicyMainBranch.lifeInsurance ||
        policy.branch === InsurancePolicyMainBranch.criticalIllness ||
        policy.branch === InsurancePolicyMainBranch.mortgageInsurance;

    // Build columns dynamically based on policy branch and isNewPolicy
    const appendixColumns = useMemo(() => {
        const columns = [
            {
                Header: t('Branch'),
                accessor: 'secondaryBranch',
                classes: 'table-client',
                Cell: AppendixBranchColumn,
                sort: false,
            },
            {
                Header: t('Person'),
                accessor: 'insuredPerson',
                classes: 'table-client',
                Cell: AppendixInsuredPersonColumn,
                sort: false,
            },
            {
                // Show "Premium (coverage)" for life/criticalIllness, just "Premium" for health
                Header: t('Premium'), //requiresCoverageAmount ? t('Premium (coverage)') : ,
                accessor: 'premium',
                classes: 'table-client',
                Cell: AppendixPremiumColumn,
                sort: false,
            },
        ];

        // Only show Coverage Amount column for life/criticalIllness
        if (requiresCoverageAmount) {
            columns.push({
                Header: t('Coverage Amount'),
                accessor: 'riskCoverageAmount',
                classes: 'table-client',
                Cell: AppendixCoverageAmountColumn,
                sort: false,
            });
        }

        // Only show Cancel appendix column for existing policies (not new)
        if (!policy.isNewPolicy) {
            columns.push({
                Header: t('Cancel appendix'),
                accessor: 'recommendation',
                classes: 'table-client',
                Cell: AppendixCancellationColumn,
                sort: false,
            });
        }

        return columns;
    }, [policy.branch, policy.isNewPolicy, requiresCoverageAmount, t]);

    const handleGeneralFormsSelected = async (forms: string[]) => {
        const prevForms = selectedForms;
        setSelectedForms(forms);
        const body = { generalTemplates: forms };

        try {
            const response = await editInsurancePolicy(policy._id!, body);
            // const editedInsurancePolicy = response.policy;
            // const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
            //     insurancePolicy._id === editedInsurancePolicy._id ? editedInsurancePolicy : insurancePolicy,
            // );
            // setInsurancePolicies(newInsurancePolicies);
            // return editedInsurancePolicy;
        } catch (error) {
            setSelectedForms(prevForms);
            globalErrorHandler(error);
        }
    };

    const handleQuestionnaireFormsSelected = async (forms: string[]) => {
        const prevForms = selectedQuestionnaireForms;
        setSelectedQuestionnaireForms(forms);
        const body = { questionnaireTemplates: forms };

        try {
            const response = await editInsurancePolicy(policy._id!, body);
            // const editedInsurancePolicy = response.policy;
            // const newInsurancePolicies = insurancePolicies.map((insurancePolicy) =>
            //     insurancePolicy._id === editedInsurancePolicy._id ? editedInsurancePolicy : insurancePolicy,
            // );
            // setInsurancePolicies(newInsurancePolicies);
        } catch (error) {
            setSelectedQuestionnaireForms(prevForms);
            globalErrorHandler(error);
        }
    };

    return (
        <div className="inner-table-container">
            {!!items.length && (
                <div className="row mb-4 mt-1">
                    {items.map(({ label, Component, extraData }, index) => (
                        <div key={index} className="col-md-6 col-lg-4 col-xl-2 mb-3 me-1">
                            <div className="d-flex flex-column">
                                <div className="fw-bold">{t(label)}</div>
                                {Component ? <Component row={{ original: policy }} extraData={extraData} /> : <></>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="row">
                <div className="col-md-6">
                    {!!appendices?.length && (
                        <Table
                            columns={appendixColumns}
                            tableClass="appendix-table"
                            data={appendices}
                            rowNumbering={false}
                            rowStyle={rowStyle}
                            headerStyle={headerStyle}
                        />
                    )}
                </div>
                <div className="col-md-3">
                    <FormSelectionCard
                        forms={generalForms}
                        onFormsChange={handleGeneralFormsSelected}
                        title={t('Select Forms')}
                        selectedForms={selectedForms}
                        disabled={policy.status !== InsurancePolicyStatuses.Initial}
                    />
                </div>
                {policy.isNewPolicy && (
                    <div className="col-md-3">
                        <FormSelectionCard
                            forms={questionnaireForms}
                            onFormsChange={handleQuestionnaireFormsSelected}
                            title={t('Add Questionnaire')}
                            selectedForms={selectedQuestionnaireForms}
                            disabled={policy.status !== InsurancePolicyStatuses.AgentFilled}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PolicyRowTableExpansion;
